import { BCMSCurrency, BCMSDate, BCMSDefaultModel, BCMSRecord, useGlobalController, useParams, Workflow } from "@bcm-dev/bcms-vite";
import { Account, Autopay, Bill, Biller, Payment } from "model";

import AddAutoPayPage from "compid/pages/AddAutoPay";
import { default as ConfirmAutoPay, default as ConfirmAutoPayDetails } from "compid/pages/ConfirmAutoPay";
import EditAutopayPage from "compid/pages/EditAutopayPage";

// Steps of the workflow
var steps = {
    // START: Adding an autopay steps.
    "/addAutopay" : <AddAutoPayPage />, // index 0
    "/confirmAutopay" : <ConfirmAutoPay />, // index 1
    // END: Adding an autopay steps.

    // START: Editing an autopay steps.
    "/editAutopay" : <EditAutopayPage/>, // index 2
    "/editAutopayConfirmation": <ConfirmAutoPayDetails/> // index 3
    // END: Editing an autopay steps.
};

export default function AddAutopayWorkflow(props) {
    // Initalize global controller
    const controller = useGlobalController();
    // Get parameters from the url
    const { billerID, stepID, autopayID } = useParams();
    // Get the default autopay and return the workflow component
    const value = stepID > 1 ? getEditAutopay(controller, billerID, autopayID) : getDefaultAutopay(controller, billerID);
    const constructor = value => {
        const { autopay: autopayValues } = value;
        const { biller, autopay, payment, bill } = getDefaultAutopay(controller, billerID);

        return {
            autopay: Autopay.init({ ...autopay.record, ...autopayValues }),
            biller,
            payment,
            bill
        }
    };

    return <Workflow value={ value } steps={ Object.values(steps) } step={ stepID } constructor={ constructor } />
}

// Hook to return the workflow steps
export const getWorkflowSteps = () => steps;

// getEditAutopay :: bcmsGlobalController, STRING -> { Biller, Autopay, Payment, BOOLEAN }
// Return the selected autopay and other info for editing in the workflow.
export const getEditAutopay = (controller, billerID, autopayID) => {
    BCMSRecord.globalControllerFor([ Biller, Autopay, Account, Payment, Bill ], controller);

    const biller = billerID === undefined ? Biller.getFirstEBiller() ?? Biller.firstInstanceOf() : Biller.initByID(billerID);
    const autopay = Autopay.initAll().filter(item => item.id === autopayID)[0];
    
    const payment = Payment.initAll().filter(item => {
        if(item.autopay) {
            return item.autopay.id === autopayID;
        }
        return false;
    })[0];

    const bill = Bill.getBillByBiller(biller.id);
    if(autopay) {
        autopay.alwaysPay = autopay.alwaysPay ? autopay.alwaysPay : BCMSCurrency.init(0);
        if(autopay.duration !== "Until a specific date") autopay.finalDeliveryBy = BCMSDate.init().offset(-1);
    }

    return { biller, autopay, payment, bill, editing: true };
}

// getDefaultAutopay :: bcmsGlobalController, STRING -> { Biller, Autopay, Payment }
// Initalize and return the default information for the workflow
export const getDefaultAutopay = (controller, billerID) => {
    BCMSRecord.globalControllerFor([ Biller, Autopay, Account, Payment, Bill ], controller);

    const biller = billerID === undefined ? Biller.getFirstEBiller() ?? Biller.firstInstanceOf() : Biller.initByID(billerID);
    const autopay = BCMSDefaultModel.autopay();
    const payment = BCMSDefaultModel.payment();
    autopay.frequency = undefined;
    autopay.duration = undefined;
    autopay.numberOfPayments = "";
    const bill = Bill.getBillByBiller(biller.id);

    autopay.id = Autopay.nextID();
    autopay.alwaysPay = BCMSCurrency.init(0);
    autopay.finalDeliveryBy = BCMSDate.init().offset(-1);

    const paymentDate = BCMSDate.init();

    if(biller.paymentType === "paper") {
        Array.apply(null, Array(5)).forEach(i => paymentDate.nextBusinessDay());
    }
    else { paymentDate.nextBusinessDay(); }

    autopay.deliverBy = paymentDate;
    autopay.billerID = biller.id;

    payment.id = Payment.nextID();
    payment.to = biller;
    payment.amount = "0";

    return { biller, autopay, payment, bill };
};